/**
 * Client-side counterpart of PHP's w2hUrl::isSafeRedirectTarget()
 * (plugins/wayToHealthPlugin/lib/toolkit/w2hUrl.class.php).
 *
 * CASA 5.1.2 (open redirection): a redirect target that came out of the URL bar, a
 * query parameter, postMessage or an API payload is attacker-controlled. Only two
 * shapes are safe to hand to window.location:
 *
 *   1. a site-relative path: a single leading "/" not followed by another "/" or a
 *      backslash (browsers normalise "\" to "/" in the authority position);
 *   2. an absolute http(s) URL whose host is the host we are currently served from,
 *      or one of an explicitly allowlisted set.
 *
 * Everything else is rejected, including absolute URLs to other hosts,
 * protocol-relative "//evil.com", "javascript:"/"data:"/any non-http(s) scheme,
 * userinfo tricks such as "https://ourhost@evil.com", backslash variants such as
 * "/\evil.com", percent-encoded variants of the above, values containing control
 * characters or whitespace, and empty or unparseable values.
 */

// Any character up to and including space, plus DEL. Browsers strip tab/CR/LF from a URL
// before resolving it, so "/<tab>/evil.com" becomes "//evil.com" and "java<tab>script:"
// becomes "javascript:"; such values are only ever smuggling attempts.
const CONTROL_OR_WHITESPACE = /[\u0000-\u0020\u007f]/;

// The percent-encoded form of the above, minus "%20": an encoded space is legitimate
// inside a query string, an encoded tab or newline never is.
const ENCODED_CONTROL = /%(?:[01][0-9A-Fa-f]|7[Ff])/;

// A "%" that is not followed by two hex digits.
const MALFORMED_ESCAPE = /%(?![0-9A-Fa-f]{2})/;

/**
 * @param {string} value
 * @returns {string} value with backslashes normalised to forward slashes
 */
function normalizeSlashes(value) {
  return value.replace(/\\/g, '/');
}

/**
 * @param {string} value
 * @returns {string|null} value percent-decoded once, or null if it is not decodable
 */
function decodeOnce(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // Malformed or non-UTF-8 escape: never a legitimate target. Deliberately not treated
    // as "undecodable, judge the raw string only" - see the PHP/JS divergence note on
    // w2hUrl::isSafeRedirectTarget() for why relaxing this would open a new gap instead.
    return null;
  }
}

/**
 * Split a "host" / "host:port" / "[::1]" / "[::1]:port" authority into its parts.
 *
 * Mirror of w2hUrl::splitHostAndPort().
 *
 * @param {string} authority
 * @returns {{host: string, port: number|null}}
 */
function splitHostAndPort(authority) {
  const value = String(authority || '').trim().toLowerCase();

  // Bracketed IPv6 literal: the port, if any, follows the closing bracket.
  if (value.startsWith('[')) {
    const close = value.indexOf(']');

    if (close === -1) {
      return {host: value, port: null};
    }

    const rest = value.slice(close + 1);
    const bracketed = /^:(\d+)$/.exec(rest);

    return {host: value.slice(0, close + 1), port: bracketed ? Number(bracketed[1]) : null};
  }

  const match = /^(.+):(\d+)$/.exec(value);

  return match ? {host: match[1], port: Number(match[2])} : {host: value, port: null};
}

/**
 * Does host[:port] under scheme match one of the allowed host entries? Origin
 * comparison, not string - mirrors PHP's w2hUrl::hostMatchesCanonical() (divergences
 * catalogued on w2hUrl::isSafeRedirectTarget()). A portless entry matches only the
 * scheme's default port; a ported entry matches only that exact port.
 *
 * @param {string} host lowercase hostname, with brackets if IPv6
 * @param {number|null} port explicit port from the target, or null
 * @param {string} scheme lowercase scheme of the target, without the colon
 * @param {string[]} allowedHosts entries from allowedRedirectHosts()
 * @returns {boolean}
 */
function hostMatchesAllowedHost(host, port, scheme, allowedHosts) {
  const defaultPort = scheme === 'https' ? 443 : 80;
  const effectivePort = port === null ? defaultPort : port;

  return allowedHosts.some((entry) => {
    const parsed = splitHostAndPort(entry);

    if (parsed.host !== host) {
      return false;
    }

    if (parsed.port === null) {
      // No port on the entry: only the scheme's default port is the same origin.
      return effectivePort === defaultPort;
    }

    return parsed.port === effectivePort;
  });
}

/**
 * @param {string} value an absolute URL candidate
 * @param {string[]} allowedHosts lowercase hosts (optionally "host:port")
 * @returns {boolean}
 */
function isAllowedAbsoluteUrl(value, allowedHosts) {
  let url;

  try {
    // eslint-disable-next-line compat/compat
    url = new URL(value);
  } catch (e) {
    return false;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false;
  }

  // "https://ourhost@evil.com/" lands on evil.com; userinfo has no legitimate use here.
  if (url.username || url.password) {
    return false;
  }

  // No port sanity check needed here: the URL constructor already throws for a port that
  // isn't a plain digit run ("host:+443"). PHP's parse_url() accepts those, so
  // w2hUrl::isCanonicalAbsoluteUrl() has an explicit check to match this behaviour.

  return hostMatchesAllowedHost(
    url.hostname.toLowerCase(),
    url.port === '' ? null : Number(url.port),
    url.protocol.replace(':', ''),
    allowedHosts
  );
}

/**
 * The hosts we are willing to navigate to: whatever we are served from, plus any
 * explicitly allowlisted extras. Empty extras is strict same-host-only, never "allow
 * anything."
 *
 * Differs from PHP's w2hUrl::canonicalHosts(): there, a configured list REPLACES the
 * request-derived host (X-Forwarded-Host-influenced); here window.location.host can't be
 * spoofed by a header, so it's always included alongside any extras.
 *
 * @param {string[]} [extraHosts] additional allowlisted hosts
 * @returns {string[]} lowercase hosts
 */
export function allowedRedirectHosts(extraHosts = []) {
  const hosts = [];

  if (typeof window !== 'undefined' && window.location && window.location.host) {
    hosts.push(window.location.host.toLowerCase());
  }

  extraHosts.forEach((host) => {
    const normalized = String(host || '').trim().toLowerCase();

    if (normalized && hosts.indexOf(normalized) === -1) {
      hosts.push(normalized);
    }
  });

  return hosts;
}

/**
 * @param {*} target untrusted redirect target
 * @param {string[]} [extraHosts] additional allowlisted hosts
 * @returns {boolean} true when it is safe to navigate to target
 */
export function isSafeRedirectTarget(target, extraHosts = []) {
  if (typeof target !== 'string') {
    return false;
  }

  const candidate = target.trim();

  if (candidate === ''
    || CONTROL_OR_WHITESPACE.test(candidate)
    || ENCODED_CONTROL.test(candidate)
    || MALFORMED_ESCAPE.test(candidate)) {
    return false;
  }

  const decoded = decodeOnce(candidate);

  if (decoded === null) {
    // A malformed percent escape means we cannot reason about what the browser will make
    // of the value, and it is never a legitimate redirect target.
    return false;
  }

  // Judge the raw value *and* a once-decoded copy, so encoded authority and scheme
  // tricks are evaluated the way a browser would evaluate them. The decoded copy is
  // deliberately not whitespace-checked: "%20" is legitimate inside a query string, and
  // the browser will not decode it before deciding which origin the URL points at.
  const forms = decoded === candidate ? [candidate] : [candidate, decoded];
  const allowedHosts = allowedRedirectHosts(extraHosts);

  return forms.every((form) => {
    // Mirror of the same guard in w2hUrl::isSafeRedirectTarget(). A backslash anywhere in
    // the leading slash run is rejected before any normalisation: browsers disagree about
    // what it means (protocol-relative, UNC-style authority, or a plain site-relative
    // path), so the only shape we can reason about is the one with no backslash at all.
    if (/^[/\\]*\\/.test(form)) {
      return false;
    }

    const normalized = normalizeSlashes(form);

    if (normalized.startsWith('//')) {
      // Protocol-relative: keeps our scheme, goes to the attacker's host.
      return false;
    }

    if (normalized.startsWith('/')) {
      // Site-relative: stays on this origin.
      return true;
    }

    return isAllowedAbsoluteUrl(form, allowedHosts);
  });
}

/**
 * @param {*} target untrusted redirect target
 * @param {string|null} [fallback] returned when target is rejected
 * @param {string[]} [extraHosts] additional allowlisted hosts
 * @returns {string|null} a target that is safe to navigate to, or fallback
 */
export default function safeRedirectTarget(target, fallback = null, extraHosts = []) {
  return isSafeRedirectTarget(target, extraHosts) ? target.trim() : fallback;
}
